import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCanActAsManager } from './useCanActAsManager';
import { subDays } from 'date-fns';

const QUERY_KEY = 'whatsapp-notifications-topbar';

export function useWhatsAppNotifications() {
  const queryClient = useQueryClient();
  const { appUserId } = useCanActAsManager();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: [QUERY_KEY, appUserId],
    queryFn: async (): Promise<number> => {
      if (!appUserId) return 0;
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();

      // Find contact_ids where this user has sent outbound messages (the "Minhas" criterion)
      const { data: sentRows } = await supabase
        .from('messages')
        .select('contact_id')
        .eq('direction', 'outbound')
        .eq('channel', 'whatsapp')
        .in('source', ['twilio_divus', 'live'])
        .eq('sent_by_app_user_id', appUserId)
        .not('contact_id', 'is', null)
        .gte('created_at', thirtyDaysAgo);

      const contactIds = [...new Set((sentRows || []).map((r) => r.contact_id).filter(Boolean))];
      if (contactIds.length === 0) return 0;

      // Count unread inbound messages from those contacts
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('direction', 'inbound')
        .eq('channel', 'whatsapp')
        .in('source', ['twilio_divus', 'live'])
        .is('read_at', null)
        .gte('created_at', thirtyDaysAgo)
        .in('contact_id', contactIds);

      if (error) {
        console.error('[useWhatsAppNotifications] Error:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!appUserId,
    staleTime: 30 * 1000,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!appUserId) return;
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();

      // Find contact_ids where this user has sent outbound messages
      const { data: sentRows } = await supabase
        .from('messages')
        .select('contact_id')
        .eq('direction', 'outbound')
        .eq('channel', 'whatsapp')
        .in('source', ['twilio_divus', 'live'])
        .eq('sent_by_app_user_id', appUserId)
        .not('contact_id', 'is', null)
        .gte('created_at', thirtyDaysAgo);

      const contactIds = [...new Set((sentRows || []).map((r) => r.contact_id).filter(Boolean))];
      if (contactIds.length === 0) return;

      const { error } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('direction', 'inbound')
        .eq('channel', 'whatsapp')
        .in('source', ['twilio_divus', 'live'])
        .in('contact_id', contactIds)
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  // Realtime subscription
  useEffect(() => {
    if (!appUserId) return;

    const channel = supabase
      .channel('whatsapp-notifications-topbar')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `channel=eq.whatsapp`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, appUserId]);

  return {
    unreadCount,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAllAsReadMutation.isPending,
  };
}
